import { articleDetailQuery } from "../../features/article/hooks/useArticleData";
import { z } from "zod";
import { useCallback, useReducer } from "react";
import { useChatScrollAnchor } from "../../domains/gpt/hooks/useChatScrollAnchor";
import { useChatKeyboardScroll } from "../../domains/gpt/hooks/useChatKeyboardScroll";
import { useLocationState } from "../../domains/app/hooks/useLocationState";
import { push } from "react-router-redirect";
import {
  displayActionableToast,
  displayToast,
} from "../../components/app/AppToast";
import { GptConversation } from "../../components/gpt/chat/GptConversation";
import { useChatSuggestion } from "../../domains/gpt/hooks/useChatSuggestion";
import { useVisualAbstractBand } from "../../features/visual-abstract/useVisualAbstractBand";
import { VisualAbstractGeneratingNotice } from "../../components/gpt/chat/VisualAbstractGeneratingNotice";
import {
  chatGroundingTitle,
  usePaperGrounding,
} from "../../domains/gpt/hooks/usePaperGrounding";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import Loader from "../../components/Loader";
import {
  useGptChat,
  toUIMessages,
  dropTurnToRetry,
  endingAnswerOf,
  type GeneralChatUIMessage,
  type QuestionOrigin,
} from "../../domains/gpt/hooks/useGptChat";
import { useFollowUps } from "../../domains/gpt/hooks/useFollowUps";
import { useCopyAnswer } from "../../domains/gpt/hooks/useCopyAnswer";
import {
  CONVERSATION_SHARE_FLAG,
  useShareConversation,
  useUnshareConversation,
} from "../../domains/gpt/hooks/useShareConversation";
import { IconShareOff } from "@tabler/icons-react";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { useSingleFlightAction } from "../../hooks/useSingleFlightAction";
import { seedId } from "../../domains/gpt/answer";
import { useTranslation } from "react-i18next";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import {
  gaEventArticleChatQuestion,
  gaEventVisualAbstractCtaShown,
  gaEventVisualAbstractShared,
  gaEventVisualAbstractShareRevoked,
  gaEventVisualAbstractUnavailable,
} from "../../tools/analytics/domains/article.analytics";
import {
  apiClient,
  useApiQuery,
  useApiRevalidate,
  type Api,
} from "../../services/api";
import { GptConversationHeader } from "../../components/gpt/GptConversationHeader";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { useKeyboardManaged } from "../../hooks/use-keyboard-managed";
import { Button } from "../../components/ui/Button";
import { VisualAbstractThumbnail } from "../../features/visual-abstract/VisualAbstract";
import { VisualAbstractBlockerNotice } from "../../features/visual-abstract/VisualAbstractBlockerNotice";
import { watchVisualAbstract } from "../../features/visual-abstract/visualAbstractWatchStore";

type ArticleBody =
  Api["get"]["/webapp/public/articles/:slug"]["output"]["body"];

type ReadyVisualAbstractDetail = Extract<
  Api["get"]["/webapp/private/visual-abstracts/:id"]["output"]["body"],
  { status: "ready" }
>;

export default function ChatWithPaperConversationPage({
  slug,
  id: _id,
  roomId,
}: {
  slug: string;
  id: string;
  roomId: string | null;
}): React.ReactElement {
  useKeyboardManaged();
  const { gptNewChatPageLink } = usePageLinks(roomId);
  const routeState = useLocationState(
    z
      .object({ question: z.string(), answer: z.string() })
      .nullable()
      .default(null)
  );
  const articleQuery = useApiQuery("/webapp/public/articles/:slug", {
    param: { slug },
    query: articleDetailQuery(roomId),
  });
  const article = articleQuery.state === "success" ? articleQuery.body : null;
  const articleId = article === null ? "" : article.id;

  const historyQuery = useApiQuery(
    "/webapp/private/articles/:id/chat-with-paper/history",
    articleId === "" ? false : { param: { id: articleId } }
  );

  // The chat hook seeds from the DB transcript, so we mount it only once both
  // the article and its history have resolved.
  if (article === null || historyQuery.state === "pending") {
    return (
      <ScrollableLayout
        background="var(--color-ds-cream)"
        headerClassName="bg-white/90 backdrop-blur-md"
        header={
          <GptConversationHeader
            title={null}
            onBack={null}
            visualAbstracts={null}
            newConversationTo={gptNewChatPageLink()}
            // Chat-with-Paper is found again by its paper, never renamed or
            // deleted on its own — a find-or-create on (user, article) would
            // rebuild the row on the next visit.
            actions={null}
          />
        }
      >
        <Loader expanded />
      </ScrollableLayout>
    );
  }

  const historyMessages =
    historyQuery.state === "success" ? historyQuery.body.messages : [];
  // Null until the first turn is persisted (the landing-page bridge below can
  // outrun the write) — the conversation-scoped actions (share) wait for it.
  const conversationId =
    historyQuery.state === "success" ? historyQuery.body.conversationId : null;
  const isShared =
    historyQuery.state === "success" && historyQuery.body.isShared;
  const initialMessages: GeneralChatUIMessage[] =
    historyMessages.length > 0
      ? toUIMessages(historyMessages)
      : // Bridge from the landing page (the just-streamed turn) while the DB
        // write settles, so the first answer is visible without a flash.
        routeState === null
        ? []
        : [
            {
              id: seedId("user", 0),
              role: "user",
              parts: [{ type: "text", text: routeState.question }],
            },
            {
              id: seedId("assistant", 0),
              role: "assistant",
              parts: [{ type: "text", text: routeState.answer }],
            },
          ];

  return (
    <Conversation
      key={articleId}
      article={article}
      roomId={roomId}
      conversationId={conversationId}
      isShared={isShared}
      initialMessages={initialMessages}
      initialTurn={routeState}
    />
  );
}

// A Regenerate carries the answer it replaces: after a disconnect, the
// transcript read back still ends with it exactly when the turn did not land.
type TurnKind =
  { kind: "ask" } | { kind: "regenerate"; replacing: string | null };

// What the server did with a failed turn: a refusal or a reported failure
// persisted nothing; after a disconnect it drains the stream and persists the
// turn without us, unless the request never reached it.
type FailedTurn = {
  turn: TurnKind;
  outcome: "persistedNothing" | "unknown";
};

/**
 * The kind of the last turn sent while it is in flight, then the last failed
 * turn, until the next send or a finished turn. A REDUCER, because the
 * transport can fail in the same tick as the send, before React has committed
 * it (the same reason as `useGptStreamRecovery`).
 */
function useFailedTurn(): {
  failedTurn: FailedTurn | null;
  onSent: (kind: TurnKind) => void;
  onFailure: (outcome: FailedTurn["outcome"]) => void;
  onSettled: () => void;
} {
  const [state, dispatch] = useReducer(
    (
      current: { inFlight: TurnKind | null; failed: FailedTurn | null },
      event:
        | { type: "sent"; kind: TurnKind }
        | { type: "failed"; outcome: FailedTurn["outcome"] }
        | { type: "settled" }
    ): { inFlight: TurnKind | null; failed: FailedTurn | null } => {
      switch (event.type) {
        case "sent":
          return { inFlight: event.kind, failed: null };
        case "failed":
          return {
            inFlight: null,
            failed:
              current.inFlight === null
                ? null
                : { turn: current.inFlight, outcome: event.outcome },
          };
        case "settled":
          return { inFlight: null, failed: null };
        default:
          throw new Error(event satisfies never);
      }
    },
    { inFlight: null, failed: null }
  );
  return {
    failedTurn: state.failed,
    onSent: (kind) => {
      dispatch({ type: "sent", kind });
    },
    onFailure: (outcome) => {
      dispatch({ type: "failed", outcome });
    },
    onSettled: () => {
      dispatch({ type: "settled" });
    },
  };
}

function Conversation({
  article,
  roomId,
  conversationId,
  isShared,
  initialMessages,
  initialTurn,
}: {
  article: ArticleBody;
  roomId: string | null;
  conversationId: string | null;
  /** A public link shows the conversation: the header offers Stop sharing. */
  isShared: boolean;
  initialMessages: GeneralChatUIMessage[];
  initialTurn: { question: string; answer: string } | null;
}): React.ReactElement {
  const { t } = useTranslation();
  const { gptNewChatPageLink } = usePageLinks(roomId);
  // Conversation sharing has the same gate as the general chat's: off → the
  // Share action stays a "coming soon" stub (no link minted).
  const isShareEnabled = useFeatureIsOn(CONVERSATION_SHARE_FLAG);
  // What the agent is actually reading for this paper — the name of the chat
  // and the follow-up rule below both hang off it.
  const grounding = usePaperGrounding(article.slug);
  const groundingKind = grounding.status === "ready" ? grounding.kind : null;
  const articleId = article.id;
  // Show the latest turn on open; never auto-follow the streamed answer.
  const { scrollRef, isAtBottom, scrollToBottom, onScroll } =
    useChatScrollAnchor({ initialToBottom: true });
  // When the keyboard opens, pad the scroller and pin it to the bottom so the
  // answer's tail clears the raised composer instead of hiding behind it.
  const { paddingBottom: keyboardPaddingBottom } =
    useChatKeyboardScroll(scrollToBottom);

  // Follow-ups only where the agent holds the paper itself — declared before
  // the chat so its `onTurnFinish` can refresh them. The rule is unchanged and
  // was never able to fire: it read `hasFullText`, which was true for every
  // article, so an abstract-grounded chat suggested follow-ups as if the whole
  // paper were behind them. Unresolved counts as "not full text", so a failed
  // grounding request costs suggestions rather than making false ones.
  const followUps = useFollowUps({
    enabled: groundingKind === "full_text",
    source: { kind: "article", articleId },
  });
  const copyAnswer = useCopyAnswer();
  const shareConversation = useShareConversation();
  const unshareConversation = useUnshareConversation();
  const revalidate = useApiRevalidate();
  const failed = useFailedTurn();
  // Owns the transcript reload after a failed turn, so a second tap does not
  // send twice while it runs.
  const resync = useSingleFlightAction();

  const chat = useGptChat({
    transport: { kind: "paper", articleId, roomId, isAnonymous: false },
    initialMessages,
    onTurnAccepted: null,
    onTurnFinish: (question, answer) => {
      failed.onSettled();
      followUps.refresh(question, answer);
      // The turn may have started a poster: asking the agent for a visual
      // abstract routes to `generateVisualAbstract`, which creates the artifact
      // server-side. The card below polls only ONCE it knows a run is in flight
      // (`refetchInterval` returns false with no artifact to watch), so without
      // this the poster a user just asked for in words would sit invisible until
      // they navigated away and back. Refreshing per finished turn is cheap and
      // needs no knowledge of which tools the turn happened to call.
      void revalidate("/webapp/private/articles/:id/visual-abstracts", {
        param: { id: articleId },
        query: { roomId },
      });
      // The turn is now persisted server-side: refresh the transcript cache so
      // a back-navigation re-seeds the full, source-hydrated history — and so
      // the conversation id lands for a transcript that had none yet (first
      // turn asked on the landing page). The on-screen view reads the live
      // `chat.messages`, not this cache, so it never disrupts the current turn.
      void revalidate("/webapp/private/articles/:id/chat-with-paper/history", {
        param: { id: articleId },
      });
    },
    // No broken-stream recovery here (see `useGptStreamRecovery`, wired on the
    // general JuisciGPT surfaces): the paper transcript endpoint exposes no
    // per-turn landmark to recognise a recovered turn by. A dropped connection
    // keeps showing the error + Retry.
    onFailure: (failure) => {
      switch (failure.kind) {
        case "rejected":
        case "failed":
          failed.onFailure("persistedNothing");
          return;
        case "disconnected":
          failed.onFailure("unknown");
          return;
        default:
          throw new Error(failure satisfies never);
      }
    },
  });

  // Takes over the composer band when the agent proposes an interest or a Room.
  // Inside a room, room proposals are suppressed (see the hook) — the surface is
  // already a partner space.
  const agentSuggestion = useChatSuggestion(chat, roomId !== null, "legacy");
  const visualAbstractBand = useVisualAbstractBand({
    articleId,
    articleTitle: article.title,
    roomId,
  });
  // The band holds ONE. The agent's proposal wins: it is about the answer just
  // given and expires with the turn, while the poster offer is ours and is true
  // for as long as the paper is. Losing the band once costs it nothing.
  const suggestion =
    agentSuggestion === null ? visualAbstractBand.suggestion : agentSuggestion;

  // Arriving from the landing page with the just-streamed first turn: the
  // hook's onTurnFinish never fired here, so fetch for that turn directly.
  useEffectOnce(() => {
    if (initialTurn !== null)
      followUps.refresh(initialTurn.question, initialTurn.answer);
  });

  // The screen after a failed turn, holding the questions the server holds, so
  // the `turnIndex` of the next requests counts them; null when it could not
  // be read (the toast says so). A failed question persisted nothing: drop it.
  // A failed Regenerate left the server's turn in place. After a disconnect
  // only the server knows: read its transcript back.
  async function screenAfterFailure(
    lost: FailedTurn
  ): Promise<GeneralChatUIMessage[] | null> {
    switch (lost.outcome) {
      case "persistedNothing":
        switch (lost.turn.kind) {
          case "ask": {
            const dropped = dropTurnToRetry(chat.messages);
            return dropped === null ? chat.messages : dropped.kept;
          }
          case "regenerate":
            return chat.messages;
          default:
            throw new Error(lost.turn satisfies never);
        }
      case "unknown":
        try {
          const { body } = await apiClient.get(
            "/webapp/private/articles/:id/chat-with-paper/history",
            { param: { id: articleId } }
          );
          return toUIMessages(body.messages);
        } catch {
          displayToast(t("Something went wrong. Please try again."), "error");
          return null;
        }
      default:
        throw new Error(lost.outcome satisfies never);
    }
  }

  async function showScreenAfterFailure(
    lost: FailedTurn
  ): Promise<GeneralChatUIMessage[] | null> {
    const screen = await screenAfterFailure(lost);
    if (screen === null) return null;
    chat.setMessages(screen);
    chat.clearError();
    failed.onSettled();
    return screen;
  }

  function submit(question: string, origin: QuestionOrigin): void {
    if (question === "") return;
    void resync.run(async () => {
      const lost = failed.failedTurn;
      const screen =
        lost === null ? chat.messages : await showScreenAfterFailure(lost);
      if (screen === null) return;
      ask(screen, question, origin);
    });
  }

  function ask(
    screen: GeneralChatUIMessage[],
    question: string,
    origin: QuestionOrigin
  ): void {
    const priorQuestionCount = screen.filter((m) => m.role === "user").length;
    // This page is only ever reached as a continuation — either the handoff
    // from the landing page after its first turn, or a resume of persisted
    // history. A conversation never *starts* here, so every question belongs
    // to existing history. (Deriving `"new"` from an empty `chat.messages`
    // would misclassify a continuation when the history fetch fails to seed.)
    gaEventArticleChatQuestion({
      article,
      question,
      conversationHistory: "old",
      questionNumber: priorQuestionCount + 1,
      questionOrigin: origin,
      engine: "legacy",
    });
    followUps.clear();
    failed.onSent({ kind: "ask" });
    void chat.sendMessage({ text: question }, { body: { origin } });
  }

  function landed(
    turn: TurnKind,
    screen: GeneralChatUIMessage[],
    questionsOnScreen: number
  ): boolean {
    switch (turn.kind) {
      case "ask":
        return (
          screen.filter((m) => m.role === "user").length >= questionsOnScreen
        );
      case "regenerate":
        return endingAnswerOf(screen) !== turn.replacing;
      default:
        throw new Error(turn satisfies never);
    }
  }

  // `replacing` is the answer the SERVER ends with. Passed in rather than read
  // here: on a Retry the screen has already lost it (the SDK trims it, and a
  // broken stream may have left a partial), and this render's `chat.messages`
  // predates the transcript just read back.
  function regenerateLastTurn(replacing: string | null): void {
    failed.onSent({ kind: "regenerate", replacing });
    void chat.regenerate();
  }

  function retryAfterError(): void {
    const lost = failed.failedTurn;
    const retry = dropTurnToRetry(chat.messages);
    if (lost === null || retry === null) return;
    void resync.run(async () => {
      const questionsOnScreen = chat.messages.filter(
        (m) => m.role === "user"
      ).length;
      const screen = await showScreenAfterFailure(lost);
      if (screen === null) return;
      switch (lost.outcome) {
        case "persistedNothing":
          break;
        // The turn landed: the server holds as many questions as the screen
        // did, or no longer ends with the answer a Regenerate replaced.
        case "unknown":
          if (landed(lost.turn, screen, questionsOnScreen)) return;
          break;
        default:
          throw new Error(lost.outcome satisfies never);
      }
      switch (lost.turn.kind) {
        // A failed Regenerate is retried as one: sent again as a question, it
        // would be appended after the server's copy.
        case "regenerate":
          regenerateLastTurn(lost.turn.replacing);
          return;
        // The failed turn's original input method is not recoverable
        // client-side.
        case "ask":
          ask(screen, retry.question, "typed");
          return;
        default:
          throw new Error(lost.turn satisfies never);
      }
    });
  }

  function comingSoon(): void {
    displayToast(t("Coming soon"), "info");
  }

  // The grounding paper belongs with the SOURCES, not the answer — it renders as
  // the CWYOP card inside the sources list (with this label).
  // `null` is not an option here: the sources list reads it as "this is not a
  // CWYOP conversation" and renumbers the citations. So an unresolved grounding
  // falls back to the FEATURE's name — the same words that label a conversation
  // in the history list and the profile — rather than to a claim about what the
  // agent read. It lasts a frame: the landing page fetched this key already.
  const cwyopPaper = {
    articleId,
    label:
      groundingKind === null
        ? t("Chat With Paper")
        : chatGroundingTitle(t, groundingKind),
  };
  const questionCount = chat.messages.filter(
    (message) => message.role === "user"
  ).length;
  // Sharing needs the persisted conversation to mint a token against; the id
  // lands with the history fetch (or the next turn's revalidation).
  const shareableConversationId = isShareEnabled ? conversationId : null;
  // No owner menu here (the paper finds the conversation again), so Stop
  // sharing sits alone in the header: the mobile one and the desktop top row.
  const stopSharing =
    shareableConversationId !== null && isShared ? (
      <button
        type="button"
        aria-label={t("Stop sharing")}
        title={t("Stop sharing")}
        className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent p-0 text-ds-black hover:bg-ds-black/6"
        onClick={() => {
          void unshareConversation(shareableConversationId);
        }}
      >
        <IconShareOff className="size-5" stroke={1.5} />
      </button>
    ) : null;

  return (
    <ScrollableLayout
      containerStyle={{ background: "var(--color-ds-cream)" }}
      background="var(--color-ds-cream)"
      headerClassName="bg-white/90 backdrop-blur-md"
      header={
        <GptConversationHeader
          // The paper IS the subject of this conversation, so it is what the
          // header names — there is no separate conversation title here.
          title={article.title}
          onBack={null}
          visualAbstracts={{ articleId, roomId, articleTitle: article.title }}
          newConversationTo={gptNewChatPageLink()}
          actions={stopSharing}
        />
      }
      scrollRef={scrollRef}
      onScroll={onScroll}
      style={{ paddingBottom: keyboardPaddingBottom }}
      hideScrollbar
    >
      <GptConversation
        chat={chat}
        leading={null}
        headerActions={stopSharing}
        // The poster's LIFECYCLE — generating, ready, failed — not the offer to
        // start one. The offer lives in the composer band above
        // (`useVisualAbstractSuggestion`), because the transcript pins the
        // latest question to the top of the viewport and streams the answer
        // into reserved slack below it: a card rendered after that answer is off
        // screen for the whole turn, which is exactly when the reader is
        // engaged. The band is fixed to the bottom and is always on it.
        afterTurns={
          <VisualAbstractCard
            articleId={articleId}
            articleTitle={article.title}
            roomId={roomId}
          />
        }
        cwyopPaper={cwyopPaper}
        showModePicker={false}
        evidenceGraphByTurnIndex={new Map()}
        evidenceCheckByRunId={new Map()}
        onOpenEvidenceSource={() => undefined}
        pendingEvidence={null}
        searchByTurnIndex={new Map()}
        pendingSearch={null}
        onOpenSearchArticle={() => undefined}
        onRefineSearch={() => undefined}
        // Chat-with-Paper never emits a Search turn (the map above stays empty),
        // so the zero-match recovery never renders here. Read-only surface: null.
        onAskGeneral={null}
        // Chat-with-Paper is scoped to ONE paper: a second attached document would
        // quietly compete with the paper the surface exists for.
        attachments={null}
        canModifyAttachments={false}
        // Same reason as attachments: CWYOP is scoped to one paper.
        references={null}
        study={null}
        canModifyReferences={false}
        onAnswerCard={null}
        answerMath={true}
        actions={{
          onCopy: (question, text) => {
            copyAnswer(question, text, () => {
              displayToast(t("Copied"), "success");
            });
          },
          onShare:
            shareableConversationId === null
              ? comingSoon
              : () => {
                  void shareConversation(
                    shareableConversationId,
                    questionCount
                  );
                },
          showShare: shareableConversationId !== null,
          onRegenerate: () => {
            regenerateLastTurn(endingAnswerOf(chat.messages));
          },
          onRetry: retryAfterError,
          // Chat-with-Paper is outside the rubric's scope: it judges an answer
          // to a clinical question against the corpus, not a conversation
          // grounded in one paper.
          onEvaluate: null,
          evaluatedTurnIndexes: [],
          comingSoon,
        }}
        followUps={{
          enabled: groundingKind === "full_text",
          questions: followUps.questions,
          onPick: (question) => {
            submit(question, "followup_suggestion");
          },
        }}
        disclaimer={t(
          "AI can make mistakes. Consider checking Source information."
        )}
        background="var(--color-ds-cream)"
        roomId={roomId}
        assistantConversationId={null}
        viewStateKey={articleId}
        onOpenSources={() => undefined}
        onOpenSource={() => undefined}
        isReconnecting={false}
        hasLostTurn={false}
        // No history drawer on Chat-with-Paper.
        followKeyboard
        composer={{
          kind: "input",
          onSubmit: (question) => {
            submit(question, "typed");
          },
          onStop: () => chat.stop(),
          placeholder: t("Keep asking..."),
        }}
        scrollRef={scrollRef}
        isAtBottom={isAtBottom}
        onScrollToBottom={scrollToBottom}
        revealAssistantId={null}
        suggestion={suggestion}
        // Chat-with-Paper is a reading surface scoped to ONE article: the user
        // came to interrogate this paper, not to set up the product. The push
        // ask belongs to the JuisciGPT conversation, which is where it fires.
        // A run in progress reports ABOVE the composer, never in the
        // transcript: the reader is waiting for it, and the transcript's tail is
        // off screen while they read the answer.
        composerNotice={
          visualAbstractBand.isGenerating ? (
            <VisualAbstractGeneratingNotice />
          ) : null
        }
        pushOptIn={null}
      />
    </ScrollableLayout>
  );
}

function VisualAbstractCard({
  articleId,
  articleTitle,
  roomId,
}: {
  articleId: string;
  /** Rides into the watch entry, so the completion toast names this paper. */
  articleTitle: string;
  roomId: string | null;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const refetchInterval = useCallback(
    (
      data:
        | Api["get"]["/webapp/private/articles/:id/visual-abstracts"]["output"]
        | undefined
    ) =>
      data !== undefined &&
      data.body.artifacts.some(
        (artifact) =>
          artifact.status === "queued" || artifact.status === "generating"
      )
        ? 3_000
        : false,
    []
  );
  const listQuery = useApiQuery(
    "/webapp/private/articles/:id/visual-abstracts",
    {
      param: { id: articleId },
      query: { roomId },
      refetchInterval,
    }
  );
  const latest =
    listQuery.state === "success" ? listQuery.body.artifacts.at(0) : undefined;
  const readyId =
    latest !== undefined && latest.status === "ready" ? latest.id : null;
  const detailQuery = useApiQuery(
    "/webapp/private/visual-abstracts/:id",
    readyId === null ? false : { param: { id: readyId } }
  );
  const readyDetail =
    detailQuery.state === "success" && detailQuery.body.status === "ready"
      ? detailQuery.body
      : null;
  const publicUrl = readyDetail === null ? null : readyDetail.publicUrl;

  // Which affordance the card is offering, or null when the card does not
  // render at all — the impression event needs the state, because the
  // click-through would otherwise be diluted by every render where the card was
  // not on screen.
  //
  // `generate` and `generating` are NOT states of this card: the early return
  // below drops both, so reporting them here would file an impression for a
  // card that rendered nothing, against the same funnel the band already
  // reports its own `trigger: "band"` impression to. Two impressions, one of
  // them invisible, on the metric the surface is judged by.
  const affordance =
    listQuery.state !== "success" ||
    listQuery.body.eligibility.state === "ineligible" ||
    latest === undefined ||
    latest.status === "queued" ||
    latest.status === "generating"
      ? null
      : latest.status === "failed"
        ? "failed"
        : "ready";
  useEffectOnce(() => {
    if (affordance === null) return;
    gaEventVisualAbstractCtaShown({
      articleId,
      roomId,
      affordance,
      trigger: "cta",
    });
  }, [articleId, roomId, affordance]);

  // The other half of `affordance === null`, and the reason it is not enough to
  // know the card did not render: an unloaded list and a REFUSED paper both
  // produce no card, and only the second is a fact about the feature's reach.
  const ineligibilityReason =
    listQuery.state === "success" &&
    listQuery.body.eligibility.state === "ineligible"
      ? listQuery.body.eligibility.reason
      : null;
  useEffectOnce(() => {
    if (ineligibilityReason === null) return;
    gaEventVisualAbstractUnavailable({
      articleId,
      roomId,
      reason: ineligibilityReason,
    });
  }, [articleId, roomId, ineligibilityReason]);

  // Catches the run this page did NOT start: JuisciGPT's `generateVisualAbstract`
  // tool dispatches without the client posting anything, and returns no id.
  // Registering whatever running artifact the list reveals means the agent's
  // generations get announced exactly like the button's.
  const runningId =
    latest !== undefined &&
    (latest.status === "queued" || latest.status === "generating")
      ? latest.id
      : null;
  useEffectOnce(() => {
    if (runningId === null) return;
    watchVisualAbstract({ id: runningId, articleId, articleTitle });
  }, [runningId, articleId, articleTitle]);

  async function regenerateVisualAbstract(id: string): Promise<void> {
    try {
      const response = await apiClient.post(
        "/webapp/private/visual-abstracts/:id/regenerate",
        { param: { id } }
      );
      watchVisualAbstract({ id: response.body.id, articleId, articleTitle });
      await listQuery.refetch();
      displayActionableToast(
        {
          title: t("Visual abstract generation restarted"),
          description: t("Follow its progress in Visual abstracts"),
        },
        "success",
        () => push("/visual-abstracts")
      );
    } catch (error: unknown) {
      displayToast(
        error instanceof Error
          ? error.message
          : t("Unable to regenerate the visual abstract"),
        "error"
      );
    }
  }

  async function shareVisualAbstract(
    artifact: ReadyVisualAbstractDetail
  ): Promise<void> {
    try {
      const response = await apiClient.post(
        "/webapp/private/visual-abstracts/:id/share",
        { param: { id: artifact.id } }
      );
      gaEventVisualAbstractShared({
        visualAbstractId: artifact.id,
        roomId: artifact.roomId,
        language: artifact.language,
        family: artifact.family,
        sourceType: artifact.sourceType,
        generatorVersion: artifact.generatorVersion,
        channel: "copy_link",
      });
      await Promise.all([listQuery.refetch(), detailQuery.refetch()]);
      await copyPublicUrl(response.body.publicUrl);
    } catch (error: unknown) {
      displayToast(
        error instanceof Error
          ? error.message
          : t("Unable to share the visual abstract"),
        "error"
      );
    }
  }

  async function revokeVisualAbstract(
    artifact: ReadyVisualAbstractDetail
  ): Promise<void> {
    try {
      await apiClient.delete("/webapp/private/visual-abstracts/:id/share", {
        param: { id: artifact.id },
      });
      gaEventVisualAbstractShareRevoked({
        visualAbstractId: artifact.id,
        roomId: artifact.roomId,
        language: artifact.language,
        family: artifact.family,
        sourceType: artifact.sourceType,
        generatorVersion: artifact.generatorVersion,
      });
      await Promise.all([listQuery.refetch(), detailQuery.refetch()]);
      displayToast(t("Public link revoked"), "success");
    } catch (error: unknown) {
      displayToast(
        error instanceof Error
          ? error.message
          : t("Unable to revoke the public link"),
        "error"
      );
    }
  }

  async function copyPublicUrl(publicUrl: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(publicUrl);
      displayToast(t("Public link copied"), "success");
    } catch {
      displayToast(t("Unable to copy the public link"), "error");
    }
  }

  if (listQuery.state !== "success") return null;
  if (listQuery.body.eligibility.state === "ineligible") return null;
  // This card is the poster's OUTCOME and nothing else. The offer belongs to the
  // composer band, and a run in progress is reported above the composer, so
  // everything before a settled artifact would render a headed, contentless
  // shell here — which is what gating the button, and then the spinner, one at
  // a time kept producing.
  if (
    latest === undefined ||
    latest.status === "queued" ||
    latest.status === "generating"
  )
    return null;

  return (
    <section className="mx-5 flex flex-col gap-3 rounded-2xl border border-ds-border-btn bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-1">
        <p className="m-0 font-dm-sans text-body font-semibold text-ds-mint-900">
          {t("Turn this paper into a visual abstract")}
        </p>
        <p className="m-0 text-footnote text-ds-slate-600">
          {t(
            "Generated from the licensed source, with evidence checks before sharing."
          )}
        </p>
      </div>

      {latest.status === "failed" && (
        <div className="flex flex-col gap-2">
          <p className="m-0 text-footnote text-secondary-raspberry-1">
            {latest.failure.message}
          </p>
          <Button
            variant="secondary"
            onClick={() => {
              void regenerateVisualAbstract(latest.id);
            }}
          >
            {t("Try again")}
          </Button>
        </div>
      )}

      {readyDetail !== null && (
        <div className="flex flex-col gap-3">
          <VisualAbstractThumbnail
            href={`/visual-abstract/${readyDetail.id}/preview`}
            view={readyDetail.view}
          />

          <VisualAbstractBlockerNotice blockers={readyDetail.blockers} />

          {readyDetail.blockers.length === 0 &&
            readyDetail.publicUrl === null && (
              <Button
                variant="accent"
                onClick={() => {
                  void shareVisualAbstract(readyDetail);
                }}
              >
                {t("Create public link")}
              </Button>
            )}

          {publicUrl !== null && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="accent"
                onClick={() => {
                  void copyPublicUrl(publicUrl);
                }}
              >
                {t("Copy public link")}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  void revokeVisualAbstract(readyDetail);
                }}
              >
                {t("Revoke link")}
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
